import { useMutation, useQueryClient } from "@tanstack/react-query";

import { reviewApi, type CandidateReview } from "../api/review";

// "answer:q2b" → "#answer-q2b"; "metric:q2b.paste_chars" → "#metrics-q2b"; "rule:x" → "#flags"
function anchorFor(evidence: string): string {
  const [kind, rest = ""] = evidence.split(":", 2);
  if (kind === "answer") return `#answer-${rest}`;
  if (kind === "metric") return `#metrics-${rest.split(".")[0]}`;
  if (kind === "rule") return "#flags";
  if (kind === "profile") return "#profile";
  return "#answers";
}

function Evidence({ ids }: { ids: string[] }) {
  return (
    <>
      {ids.map((id) => (
        <a key={id} href={anchorFor(id)} className="evidence">
          [{id}]
        </a>
      ))}
    </>
  );
}

const STATUS_TEXT: Record<string, string> = {
  generating: "Generating…",
  waiting:
    "Waits until telemetry has settled (shortly after submission), then generates automatically.",
  budget_used: "Not available: this application's AI budget (2 calls) is used.",
  failed: "The last attempt failed.",
};

export function AiSummary({ review }: { review: CandidateReview }) {
  const queryClient = useQueryClient();
  const summary = review.summary;
  const generate = useMutation({
    mutationFn: () => reviewApi.generateSummary(review.header.public_id),
    onSuccess: (updated) =>
      queryClient.setQueryData(["review", "candidate", review.header.public_id], updated),
  });
  if (!summary || summary.mode === "off") return null;
  const canGenerate = summary.status === "available" || summary.status === "failed";

  return (
    <details id="ai-summary" className="ai-summary">
      <summary>AI summary — AI-generated, may be wrong. Read the record above first.</summary>
      {summary.status === "ready" && summary.content ? (
        <>
          <h3>Observations</h3>
          <ul>
            {summary.content.thinking.map((o, i) => (
              <li key={i}>
                <strong>{o.question.toUpperCase()}</strong>: {o.observation}{" "}
                <Evidence ids={o.evidence} />
              </li>
            ))}
          </ul>
          {summary.content.flags.length > 0 && (
            <>
              <h3>Points to check</h3>
              <ul>
                {summary.content.flags.map((f, i) => (
                  <li key={i}>
                    {f.kind}: {f.text} <Evidence ids={f.evidence} />
                  </li>
                ))}
              </ul>
            </>
          )}
          <h3>Questions for the interview</h3>
          <ul>
            {summary.content.questions_for_interview.map((q, i) => (
              <li key={i}>{q}</li>
            ))}
          </ul>
          <p className="muted">Model: {summary.model}. No decision is made by AI.</p>
        </>
      ) : (
        <>
          {STATUS_TEXT[summary.status] && <p>{STATUS_TEXT[summary.status]}</p>}
          {canGenerate && (
            <button type="button" disabled={generate.isPending} onClick={() => generate.mutate()}>
              {generate.isPending ? "Generating… (up to a minute)" : "Generate summary"}
            </button>
          )}
          {generate.isError && <p role="alert">{generate.error.message}</p>}
        </>
      )}
    </details>
  );
}
